<template>
  <div>
    <v-data-table
      :headers="computedHeaders"
      :items="customers"
      :options="tableOptions"
      :loading="loading"
      :custom-sort="customSort"
      :footer-props="{
        itemsPerPageText: '行/ページ:',
        itemsPerPageOptions: [10, 25, 50],
      }"
      no-data-text="データがありません"
      loading-text="読込中..."
      class="customer-list-table"
      @click:row="onCustomerClick"
      @update:options="onOptionsUpdate"
    >
      <template #[`item.attributes.memberNumber`]="{ item }">
        {{ item.attributes.memberNumber || '-' }}
      </template>

      <template #[`item.attributes.name`]="{ item }">
        <div>
          <div>{{ item.attributes.name }}</div>
          <div
            v-if="item.attributes.customerTags && item.attributes.customerTags.length > 0"
            class="mt-1"
          >
            <v-chip
              v-for="tag in item.attributes.customerTags.slice(0, 3)"
              :key="tag.id"
              x-small
              outlined
              class="mr-1"
            >
              {{ tag.name }}
            </v-chip>
            <span
              v-if="item.attributes.customerTags.length > 3"
              class="text-caption grey--text"
            >
              +{{ item.attributes.customerTags.length - 3 }}
            </span>
          </div>
        </div>
      </template>

      <template #[`item.attributes.receiptAddressee`]="{ item }">
        {{ item.attributes.receiptAddressee || '-' }}
      </template>

      <template #[`item.attributes.visitCount`]="{ item }">
        {{ item.attributes.visitCount || 0 }}
      </template>

      <template #[`item.attributes.lastVisitedAt`]="{ item }">
        {{ formatDateTime(item.attributes.lastVisitedAt) }}
      </template>

      <template #[`item.attributes.birthday`]="{ item }">
        {{ formatBirthday(item.attributes.birthday) }}
      </template>

      <template #[`item.attributes.casts`]="{ item }">
        <div v-if="item.attributes.casts && item.attributes.casts.length > 0">
          <v-chip
            v-for="cast in item.attributes.casts.slice(0, 2)"
            :key="cast.id"
            x-small
            outlined
            class="mr-1"
          >
            {{ cast.name }}
          </v-chip>
          <span
            v-if="item.attributes.casts.length > 2"
            class="text-caption grey--text"
          >
            +{{ item.attributes.casts.length - 2 }}
          </span>
        </div>
        <span v-else>-</span>
      </template>

      <!-- カスタムフィールド列 -->
      <template
        v-for="field in customFields"
        #[`item.customField_${field.id}`]="{ item }"
      >
        <span :key="field.id">{{ getCustomFieldValue(item, field) }}</span>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-menu
          offset-y
          left
        >
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              @click.stop
            >
              <v-icon small>
                {{ icons.mdiDotsVertical }}
              </v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click.stop="onCustomerClick(item)">
              <v-list-item-icon class="mr-2">
                <v-icon small>
                  {{ icons.mdiAccountDetails }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-title>詳細を見る</v-list-item-title>
            </v-list-item>
            <v-list-item @click.stop="onCreateTable(item)">
              <v-list-item-icon class="mr-2">
                <v-icon small>
                  {{ icons.mdiTablePlus }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-title>卓を作成</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import {
  mdiDotsVertical, mdiAccountDetails, mdiTablePlus,
} from '@mdi/js'
import { format } from 'date-fns'
import { formatDateTime } from '@/utils/formatters'

export default {
  name: 'CustomerList',
  props: {
    customers: {
      type: Array,
      default: () => [],
      required: true,
    },
    customFields: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const tableOptions = ref({
      page: 1,
      itemsPerPage: 50,
    })

    const onOptionsUpdate = options => {
      tableOptions.value = options
    }

    // 誕生日を月日（MMDD）に変換してソート用の数値を返す
    const birthdayToMonthDay = dateStr => {
      if (!dateStr) return null
      const d = new Date(dateStr)

      // MM * 100 + DD で月日のみの数値化
      return (d.getMonth() + 1) * 100 + d.getDate()
    }

    // ネストされたプロパティを取得するヘルパー
    const getNestedValue = (obj, path) => path.split('.').reduce((o, k) => (o || {})[k], obj)

    const customSort = (items, sortBy, sortDesc) => {
      if (!sortBy || sortBy.length === 0) return items

      const key = sortBy[0]
      const desc = sortDesc[0]

      return [...items].sort((a, b) => {
        let valA
        let valB

        if (key === 'attributes.birthday') {
          valA = birthdayToMonthDay(getNestedValue(a, key))
          valB = birthdayToMonthDay(getNestedValue(b, key))
        } else {
          valA = getNestedValue(a, key)
          valB = getNestedValue(b, key)
        }

        // null/undefinedは末尾に
        if (valA == null && valB == null) return 0
        if (valA == null) return 1
        if (valB == null) return -1

        let result
        if (typeof valA === 'number' && typeof valB === 'number') {
          result = valA - valB
        } else {
          result = String(valA).localeCompare(String(valB), 'ja')
        }

        return desc ? -result : result
      })
    }

    const baseHeaders = [
      {
        text: '会員番号',
        value: 'attributes.memberNumber',
        sortable: true,
        width: '100',
      },
      {
        text: '名前',
        value: 'attributes.name',
        sortable: true,
        width: '150',
      },
      {
        text: '領収書宛名',
        value: 'attributes.receiptAddressee',
        sortable: false,
        width: '150',
      },
      {
        text: '来店回数',
        value: 'attributes.visitCount',
        sortable: true,
        width: '100',
      },
      {
        text: '最終来店日',
        value: 'attributes.lastVisitedAt',
        sortable: true,
        width: '150',
      },
      {
        text: '誕生日',
        value: 'attributes.birthday',
        sortable: true,
        width: '100',
      },
      {
        text: '関連キャスト',
        value: 'attributes.casts',
        sortable: false,
        width: '150',
      },
    ]

    const actionsHeader = {
      text: '',
      value: 'actions',
      sortable: false,
      width: '50',
      align: 'end',
    }

    const computedHeaders = computed(() => {
      // カスタムフィールド列を生成
      const customFieldHeaders = props.customFields.map(field => ({
        text: field.name,
        value: `customField_${field.id}`,
        sortable: false,
        width: '120',
      }))

      return [...baseHeaders, ...customFieldHeaders, actionsHeader]
    })

    const formatBirthday = dateString => {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)

        return format(date, 'M/d')
      } catch (e) {
        return dateString
      }
    }

    const getCustomFieldValue = (customer, field) => {
      const customFieldValues = customer.attributes?.customFieldValues?.data || []
      const valueObj = customFieldValues.find(
        v => String(v.attributes?.customerCustomFieldId) === String(field.id),
      )

      if (!valueObj) return '-'

      const { value, typedValue } = valueObj.attributes

      // boolean型の場合
      if (field.type === 'boolean') {
        return typedValue === true ? 'はい' : 'いいえ'
      }

      return value || '-'
    }

    const onCustomerClick = customer => {
      emit('click', customer)
    }

    const onCreateTable = customer => {
      emit('create-table', customer)
    }

    return {
      tableOptions,
      computedHeaders,
      formatDateTime,
      formatBirthday,
      getCustomFieldValue,
      onCustomerClick,
      onCreateTable,
      onOptionsUpdate,
      customSort,
      icons: {
        mdiDotsVertical,
        mdiAccountDetails,
        mdiTablePlus,
      },
    }
  },
}
</script>

<style scoped>
/* 行をクリック可能にする */
::v-deep .v-data-table__wrapper tbody tr {
  cursor: pointer;
}

/* ホバー時のハイライト */
::v-deep .v-data-table__wrapper tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* タッチ対応: ヘッダー行の高さ拡大 */
::v-deep .v-data-table-header th {
  height: 52px !important;
  padding: 0 12px !important;
}

/* タッチ対応: テーブル行の高さ拡大 */
::v-deep .v-data-table__wrapper tbody td {
  height: 52px !important;
  padding: 8px 12px !important;
}
</style>
